// Recording an invoice received from a supplier.
//
// The amounts are typed as printed on the document rather than derived from
// lines: a received invoice may mix VAT rates, or carry none. The total is
// shown live as net + tax so a typo is obvious before saving.
//
// The file can only be attached once the row exists, because it is stored under
// the invoice's id — so on a new invoice the upload appears after the save.

import { useRef, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Icon } from './Icon';
import { ErrorAlert, Modal, SelectField, TextAreaField, TextField } from './ui';
import { SupplierForm } from './SupplierForm';
import { formatMoney, roundMoney } from '@shared/format';
import {
  SUPPLIER_INVOICE_STATUSES, type Supplier, type SupplierInvoice, type SupplierInvoiceStatus,
} from '@shared/types';

export function PurchaseForm({ purchase, onSaved, onCancel }: {
  purchase?: SupplierInvoice;
  onSaved: (created: boolean) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const { money } = useSession();
  const toMessage = useErrorMessage();
  const fileInput = useRef<HTMLInputElement>(null);

  const suppliers = useAsync(() => api.suppliers({ perPage: 100, activeOnly: true }), []);

  const [current, setCurrent] = useState(purchase);
  const [supplierId, setSupplierId] = useState<number | ''>(purchase?.supplierId ?? '');
  const [number, setNumber] = useState(purchase?.number ?? '');
  const [issuedOn, setIssuedOn] = useState(purchase?.issuedOn ?? new Date().toISOString().slice(0, 10));
  const [receivedOn, setReceivedOn] = useState(purchase?.receivedOn ?? '');
  const [dueOn, setDueOn] = useState(purchase?.dueOn ?? '');
  const [description, setDescription] = useState(purchase?.description ?? '');
  const [subtotal, setSubtotal] = useState(purchase ? String(purchase.subtotal) : '');
  const [taxTotal, setTaxTotal] = useState(purchase ? String(purchase.taxTotal) : '');
  const [status, setStatus] = useState<SupplierInvoiceStatus>(purchase?.status ?? 'pending');
  const [paidOn, setPaidOn] = useState(purchase?.paidOn ?? '');
  const [notes, setNotes] = useState(purchase?.notes ?? '');

  const [newSupplier, setNewSupplier] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  // A comma is what a Spanish keyboard produces for decimals.
  const asNumber = (value: string) => Number(value.replace(',', '.')) || 0;
  const total = roundMoney(asNumber(subtotal) + asNumber(taxTotal));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    setFormError(undefined);
    const payload = {
      supplierId: Number(supplierId),
      number: number.trim(),
      issuedOn,
      receivedOn: receivedOn || null,
      dueOn: dueOn || null,
      description: description.trim(),
      subtotal: asNumber(subtotal),
      taxTotal: asNumber(taxTotal),
      status,
      paidOn: status === 'paid' ? (paidOn || issuedOn) : null,
      notes,
    };
    try {
      const saved = current
        ? await api.updatePurchase(current.id, payload)
        : await api.createPurchase(payload);
      setCurrent(saved);
      onSaved(!current);
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function onFileChange(file: File | undefined) {
    if (!file || !current) return;
    setUploading(true);
    try {
      setCurrent(await api.uploadPurchaseFile(current.id, file));
    } catch (err) {
      setFormError(fieldErrors(err).file ?? toMessage(err));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function removeFile() {
    if (!current) return;
    setUploading(true);
    try {
      setCurrent(await api.deletePurchaseFile(current.id));
    } catch (err) {
      setFormError(toMessage(err));
    } finally {
      setUploading(false);
    }
  }

  const supplierOptions: Supplier[] = suppliers.data?.items ?? [];

  return (
    <>
      <form onSubmit={onSubmit} noValidate className="stack">
        {formError && <ErrorAlert message={formError} />}

        <div className="form-grid form-grid--2">
          <div className="field span-all">
            <label className="field__label" htmlFor="purchase-supplier">
              {t('purchases.field.supplier')}
            </label>
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <select
                id="purchase-supplier"
                className={`select${errors.supplierId ? ' select--error' : ''}`}
                value={supplierId}
                onChange={(e) => setSupplierId(Number(e.target.value))}
              >
                <option value="" disabled>—</option>
                {supplierOptions.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </select>
              <button type="button" className="btn btn--sm nowrap" onClick={() => setNewSupplier(true)}>
                <Icon name="plus" size={14} />{t('suppliers.new')}
              </button>
            </div>
            {errors.supplierId && <div className="field__error">{errors.supplierId}</div>}
          </div>

          <TextField label={t('purchases.field.number')} required hint={t('purchases.field.numberHint')}
            value={number} error={errors.number} onChange={(e) => setNumber(e.target.value)} />
          <TextField label={t('purchases.field.issuedOn')} type="date" required
            value={issuedOn} error={errors.issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
          <TextField label={t('purchases.field.receivedOn')} type="date" optional
            value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
          <TextField label={t('purchases.field.dueOn')} type="date" optional
            value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          <TextField label={t('purchases.field.description')} optional wrapperClassName="span-all"
            value={description} onChange={(e) => setDescription(e.target.value)} />

          <TextField label={`${t('purchases.field.subtotal')} (${money.symbol})`} required numeric
            inputMode="decimal" value={subtotal} error={errors.subtotal}
            onChange={(e) => setSubtotal(e.target.value)} />
          <TextField label={`${t('purchases.field.taxTotal')} (${money.symbol})`} required numeric
            inputMode="decimal" hint={t('purchases.amountsHint')} value={taxTotal}
            error={errors.taxTotal} onChange={(e) => setTaxTotal(e.target.value)} />

          <div className="field span-all">
            <span className="field__label">{t('purchases.field.total')}</span>
            <div className="purchase-total">{formatMoney(total, money)}</div>
          </div>

          <SelectField label={t('purchases.field.status')} value={status}
            onChange={(e) => setStatus(e.target.value as SupplierInvoiceStatus)}>
            {SUPPLIER_INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(s === 'paid' ? 'status.paidPurchase' : 'status.pendingPayment')}
              </option>
            ))}
          </SelectField>
          {status === 'paid' && (
            <TextField label={t('purchases.field.paidOn')} type="date" optional
              value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          )}

          <TextAreaField label={t('purchases.field.notes')} optional rows={2} wrapperClassName="span-all"
            value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        {/* The file lives under the invoice's id, so it can only be attached
            once the row exists. */}
        {current ? (
          <div className="field">
            <span className="field__label">{t('purchases.file')}</span>
            {current.file ? (
              <div className="row">
                <a className="badge badge--accent" href={api.purchaseFileUrl(current.id)}>
                  <Icon name="file-text" size={13} />
                  {current.file.name}
                </a>
                <span className="subtle">{Math.round(current.file.size / 1024)} KB</span>
                <label className="btn btn--sm" htmlFor="purchase-file">
                  {uploading ? <span className="spinner" /> : <Icon name="image" size={14} />}
                  {t('purchases.fileReplace')}
                </label>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => void removeFile()}
                  disabled={uploading}>
                  <Icon name="trash" size={14} />{t('purchases.fileRemove')}
                </button>
              </div>
            ) : (
              <div className="row">
                <span className="subtle">{t('purchases.fileNone')}</span>
                <label className="btn btn--sm" htmlFor="purchase-file">
                  {uploading ? <span className="spinner" /> : <Icon name="file-plus" size={14} />}
                  {t('purchases.fileUpload')}
                </label>
              </div>
            )}
            <input ref={fileInput} id="purchase-file" type="file" className="sr-only"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(e) => void onFileChange(e.target.files?.[0])} />
            <div className="field__hint">{t('purchases.fileHint')}</div>
          </div>
        ) : (
          <p className="field__hint">{t('purchases.fileHint')}</p>
        )}

        <div className="row row--end">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            {current ? t('common.close') : t('common.cancel')}
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy && <span className="spinner" />}
            {busy ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>

      {newSupplier && (
        <Modal title={t('suppliers.new')} onClose={() => setNewSupplier(false)}>
          <SupplierForm
            onCancel={() => setNewSupplier(false)}
            onSaved={(saved) => {
              setSupplierId(saved.id);
              setNewSupplier(false);
              suppliers.reload();
            }}
          />
        </Modal>
      )}
    </>
  );
}
